import { useMutation } from '@apollo/client';
import {
  AtSymbolIcon,
  CheckIcon,
  PencilSquareIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { type ReactNode, useState } from 'react';

import { updateLeadById } from 'queries/leads';

import { useUI } from 'providers/ui';

type EditableLeadContactFieldProps = {
  type: 'phone' | 'email';
  value?: string | null;
  leadId: string;
  content: Record<string, unknown> | null | undefined;
  onUpdated: () => void;
  onPhoneClick?: (formattedPhone: string) => void;
  onEmailClick?: (email: string) => void;
  compact?: boolean;
  actions?: ReactNode;
};

const formatPhoneDisplay = (phone: string) => phone.replace(/^0/, '+44');

export default function EditableLeadContactField({
  type,
  value,
  leadId,
  content,
  onUpdated,
  onPhoneClick,
  onEmailClick,
  compact = false,
  actions,
}: EditableLeadContactFieldProps) {
  const ui = useUI();
  const [updateLead, { loading }] = useMutation(updateLeadById);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const Icon = type === 'phone' ? PhoneIcon : AtSymbolIcon;
  const label = type === 'phone' ? 'Phone' : 'Email';
  const trimmedValue = value?.trim() || '';
  const displayPhone = trimmedValue ? formatPhoneDisplay(trimmedValue) : '';

  function startEditing() {
    setDraft(trimmedValue);
    setEditing(true);
  }

  function cancelEditing() {
    setDraft(trimmedValue);
    setEditing(false);
  }

  async function save() {
    const nextValue = draft.trim();

    if (type === 'email' && nextValue && !nextValue.includes('@')) {
      ui.setToast(true, {
        type: 'error',
        title: 'Invalid email',
        message: 'Enter a valid email address.',
      });
      return;
    }

    try {
      await updateLead({
        variables: {
          id: leadId,
          data: {
            content: {
              ...(content || {}),
              [type]: nextValue || null,
            },
          },
        },
      });
      setEditing(false);
      onUpdated();
      ui.setToast(true, {
        type: 'success',
        title: `${label} updated`,
        message: '',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: `Failed to update ${label.toLowerCase()}`,
        message: `${error}`,
      });
    }
  }

  if (compact) {
    return (
      <div className="inline-flex max-w-full items-center gap-2">
        <Icon
          className="h-4 w-4 flex-shrink-0 text-gray-400"
          aria-hidden="true"
        />
        <div className="min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                type={type === 'email' ? 'email' : 'tel'}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    void save();
                  } else if (event.key === 'Escape') {
                    cancelEditing();
                  }
                }}
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => void save()}
                disabled={loading}
                className="rounded p-1 text-purple-600 hover:text-purple-700 disabled:opacity-50"
                title="Save"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={loading}
                className="rounded p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                title="Cancel"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-900">
              {type === 'phone' ? (
                trimmedValue ? (
                  <button
                    type="button"
                    onClick={() => onPhoneClick?.(displayPhone)}
                    className="whitespace-nowrap font-medium text-gray-900 hover:text-purple-700"
                  >
                    {displayPhone}
                  </button>
                ) : (
                  <span className="text-gray-400">Add phone</span>
                )
              ) : trimmedValue ? (
                onEmailClick ? (
                  <button
                    type="button"
                    onClick={() => onEmailClick(trimmedValue)}
                    className="whitespace-nowrap font-medium text-gray-900 hover:text-purple-700"
                  >
                    {trimmedValue}
                  </button>
                ) : (
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href={`https://mail.google.com/mail/?view=cm&fs=1&to=${trimmedValue}`}
                    className="whitespace-nowrap font-medium text-gray-900 hover:text-purple-700"
                  >
                    {trimmedValue}
                  </a>
                )
              ) : (
                <span className="text-gray-400">Add email</span>
              )}
            </p>
          )}
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={startEditing}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-purple-600"
            title={`Edit ${label.toLowerCase()}`}
          >
            <PencilSquareIcon className="h-3.5 w-3.5" />
          </button>
        ) : null}
        {!editing && actions ? (
          <div className="flex flex-shrink-0 items-center gap-1 border-l border-gray-200 pl-2">
            {actions}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1">
      <Icon
        className="mt-0.5 flex-shrink-0 h-4 w-4 text-purple-600"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              type={type === 'email' ? 'email' : 'tel'}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void save();
                } else if (event.key === 'Escape') {
                  cancelEditing();
                }
              }}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              autoFocus
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => void save()}
              disabled={loading}
              className="rounded p-1 text-purple-600 hover:text-purple-700 disabled:opacity-50"
              title="Save"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={cancelEditing}
              disabled={loading}
              className="rounded p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              title="Cancel"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <p className="text-xs font-medium text-gray-500 truncate">
            {label}:{' '}
            {type === 'phone' ? (
              trimmedValue ? (
                <button
                  type="button"
                  onClick={() => onPhoneClick?.(displayPhone)}
                  className="text-purple-700 underline"
                >
                  {displayPhone}
                </button>
              ) : (
                <span className="text-gray-400">Not set</span>
              )
            ) : trimmedValue ? (
              onEmailClick ? (
                <button
                  type="button"
                  onClick={() => onEmailClick(trimmedValue)}
                  className="text-purple-700 underline"
                >
                  {trimmedValue}
                </button>
              ) : (
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${trimmedValue}`}
                  className="text-purple-700 underline"
                >
                  {trimmedValue}
                </a>
              )
            ) : (
              <span className="text-gray-400">Not set</span>
            )}
          </p>
        )}
      </div>
      {!editing ? (
        <button
          type="button"
          onClick={startEditing}
          className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50"
          title={`Edit ${label.toLowerCase()}`}
        >
          <PencilSquareIcon className="h-3.5 w-3.5 text-purple-600" />
        </button>
      ) : null}
    </div>
  );
}
